import type { AppDefinition } from 'owa-graph-schema';
import type {
    IStaticTab,
    IAppManifest,
} from '@metaos/hub-sdk/dist/Services/hosted-web-content-sdk.interface';

/* We need to transform Schema.AppDefinition (auto generated typings from gql schema)
 * to @metaos/hub-sdk's IAppManifest.
 *
 * The way these two deals with optionals are subtly different:
 *  - gql-code-gen defines an optional GQL field of type T as a Maybe<T> = T | null
 *  - TS with strictNullChecks defines an optional field as field?: T = T | undefined
 *  In order to achieve assignment compatibility, an optional GQL field needs to be type-assigned
 *  to an optional TS field. To do that, we can force a loosen up null matching by accepting
 *  undefined for gql-gen'd typed fields -- complying to TS standards.
 *  PS.: We could redefine gql-gen'd maybeValue config to allow undefined, as a more definitive solution to this issue:
 *  https://www.graphql-code-generator.com/docs/plugins/typescript
 */
type CastLooseOptional<T> = T extends null ? Exclude<T, null> | undefined : CastLooseOptionals<T>;
type CastLooseOptionals<T> = {
    [P in keyof T]: CastLooseOptional<T[P]>;
};

function isObject(o: any): boolean {
    return o && typeof o === 'object';
}

function isObjectArrayLike(o: any): boolean {
    return isObject(o) && Number.isFinite(o.length) && o.length >= 0 && Number.isInteger(o.length);
}

function removeProperties(obj: any, predicate: (key: string, value: any) => boolean): any {
    const newObj: object = {};
    for (let [prop, value] of Object.entries(obj)) {
        if (predicate(prop, value)) {
            continue;
        }

        if (!isObject(value)) {
            newObj[prop] = value;
            continue;
        }

        if (isObjectArrayLike(value)) {
            const newArr: any[] = (value as any[]).map((elem: any) => {
                return isObject(elem) ? removeProperties(elem as object, predicate) : elem;
            });
            newObj[prop] = newArr;
        } else {
            newObj[prop] = removeProperties(value as object, predicate);
        }
    }
    return newObj;
}

export function convertAppDefinitionToManifest(
    appDefinition: CastLooseOptional<AppDefinition>
): IAppManifest {
    const appManifest: IAppManifest = {
        ...appDefinition,
        appId: appDefinition.id,
        staticTabs: appDefinition.staticTabs as IStaticTab[],
        showTabLoadingIndicator: !!appDefinition.showLoadingIndicator,
    };
    return removeProperties(appManifest, (key: string, value: any) => {
        return (
            key === '__typename' ||
            value === undefined ||
            value === null ||
            Number.isNaN(value) ||
            (value.length && value.length === 0)
        );
    });
}
